/**
 * Admin — 静态 USDT 兑付：批次导入 + 审核队列
 */
import { useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

type BatchRow = {
  id: number;
  code: string;
  register_date: string;
  guide_unit_usd: string;
  ep1_ratio: string;
  quota_usd_per_user: string;
  account_count: number;
  total_quota_usd: string;
  status: string;
  allowlist_count: number;
  request_count: number;
};

type RequestRow = {
  id: number;
  username: string;
  amount_usd: string;
  amount_ep1: string;
  wallet_address: string;
  status: string;
  payout_ref: string | null;
  review_note: string | null;
  batch_code: string;
  create_time: string;
};

const STATUS_TAG: Record<string, { color: string; text: string }> = {
  pending: { color: 'orange', text: '待审核' },
  processing: { color: 'blue', text: '链上处理中' },
  approved: { color: 'green', text: '已通过' },
  rejected: { color: 'red', text: '已拒绝' },
  cancelled: { color: 'default', text: '已取消' },
};

export function StaticUsdtPayoutPage() {
  const [status, setStatus] = useState<string | undefined>('pending');
  const [approveTarget, setApproveTarget] = useState<RequestRow | null>(null);
  const [payoutRef, setPayoutRef] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<RequestRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [importing, setImporting] = useState(false);
  const [importForm] = Form.useForm();
  const hasNode = useAuthStore((s) => s.hasNode);
  const qc = useQueryClient();

  const canApprove = hasNode('platform.withdraw.approve');
  const canReject = hasNode('platform.withdraw.reject');

  const batchesQuery = useQuery({
    queryKey: ['admin', 'static-usdt-payout', 'batches'],
    queryFn: () => api.get<BatchRow[]>('/api/admin/static-usdt-payout/batches'),
  });
  const configQuery = useQuery({
    queryKey: ['admin', 'static-usdt-payout', 'config'],
    queryFn: () =>
      api.get<{ auto_mock_payout: boolean }>('/api/admin/static-usdt-payout/config'),
  });
  const autoMockPayout = configQuery.data?.auto_mock_payout === true;

  async function runImport(values: {
    register_range: [dayjs.Dayjs, dayjs.Dayjs];
    guide_unit_usd: number;
    ep1_ratio: number;
  }) {
    setImporting(true);
    try {
      const [from, to] = values.register_range;
      const data = await api.post<{
        allowlist_count: number;
        total_quota_usd: string;
        inserted: number;
        code: string;
        register_date_from?: string;
        register_date_to?: string;
      }>('/api/admin/static-usdt-payout/batches/import-from-forecast', {
        register_date_from: from.format('YYYY-MM-DD'),
        register_date_to: to.format('YYYY-MM-DD'),
        guide_unit_usd: String(values.guide_unit_usd),
        ep1_ratio: String(values.ep1_ratio),
        close_other_batches: true,
      });
      message.success(
        `已导入 ${data.code}：白名单 ${data.allowlist_count} 户，总额 $${data.total_quota_usd}（新增 ${data.inserted}）`,
      );
      void qc.invalidateQueries({ queryKey: ['admin', 'static-usdt-payout'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '导入失败');
    } finally {
      setImporting(false);
    }
  }

  async function approve() {
    if (!approveTarget) return;
    setApproving(true);
    try {
      await api.post(`/api/admin/static-usdt-payout/requests/${approveTarget.id}/approve`, {
        payout_ref: payoutRef,
      });
      message.success(
        autoMockPayout
          ? `已自动发送 MockUSDT 并通过 #${approveTarget.id}`
          : `已通过 #${approveTarget.id}`,
      );
      setApproveTarget(null);
      setPayoutRef('');
      void qc.invalidateQueries({ queryKey: ['admin', 'static-usdt-payout'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '通过失败');
    } finally {
      setApproving(false);
    }
  }

  async function reject() {
    if (!rejectTarget) return;
    try {
      await api.post(`/api/admin/static-usdt-payout/requests/${rejectTarget.id}/reject`, {
        reason: rejectReason,
      });
      message.success(`已拒绝 #${rejectTarget.id}`);
      setRejectTarget(null);
      setRejectReason('');
      void qc.invalidateQueries({ queryKey: ['admin', 'static-usdt-payout'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '拒绝失败');
    }
  }

  const latest = batchesQuery.data?.[0];

  return (
    <div>
      <Typography.Title level={4}>静态 USDT 兑付</Typography.Title>
      <Typography.Paragraph type="secondary">
        指导销售批次固定额度（默认注册区间 2025-06-23～今天 × $100 × 52% = 每户 $52）。用户申请扣
        EP1，运营人工打款后填链上凭证通过。导入新批次时会关闭其他 active 批次。
      </Typography.Paragraph>

      <Card title="① 从测算导入白名单" style={{ marginBottom: 16 }}>
        <Form
          form={importForm}
          layout="inline"
          initialValues={{
            register_range: [dayjs('2025-06-23'), dayjs()],
            guide_unit_usd: 100,
            ep1_ratio: 0.52,
          }}
          onFinish={runImport}
        >
          <Form.Item name="register_range" label="注册区间" rules={[{ required: true }]}>
            <DatePicker.RangePicker />
          </Form.Item>
          <Form.Item name="guide_unit_usd" label="指导单价 $" rules={[{ required: true }]}>
            <InputNumber min={0} step={1} />
          </Form.Item>
          <Form.Item name="ep1_ratio" label="EP1 比例" rules={[{ required: true }]}>
            <InputNumber min={0} max={1} step={0.01} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={importing} disabled={!canApprove}>
              导入 / 刷新白名单
            </Button>
          </Form.Item>
        </Form>
        {latest && (
          <Space size="large" style={{ marginTop: 16 }}>
            <Statistic title="批次" value={latest.code} />
            <Statistic title="白名单" value={latest.allowlist_count} />
            <Statistic title="每户额度 $" value={latest.quota_usd_per_user} />
            <Statistic title="总额 $" value={latest.total_quota_usd} />
          </Space>
        )}
        <Table
          style={{ marginTop: 16 }}
          size="small"
          rowKey="id"
          loading={batchesQuery.isLoading}
          dataSource={batchesQuery.data ?? []}
          pagination={false}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 70 },
            { title: 'code', dataIndex: 'code' },
            { title: '注册日', dataIndex: 'register_date', width: 120 },
            { title: '白名单', dataIndex: 'allowlist_count', width: 90 },
            { title: '每户$', dataIndex: 'quota_usd_per_user', width: 90 },
            { title: '总额$', dataIndex: 'total_quota_usd', width: 100 },
            { title: '状态', dataIndex: 'status', width: 90 },
          ]}
        />
      </Card>

      <Card title="② 审核队列">
        <Space style={{ marginBottom: 12 }}>
          <Select
            allowClear
            placeholder="状态"
            style={{ width: 140 }}
            value={status}
            onChange={(v) => setStatus(v)}
            options={[
              { value: 'pending', label: '待审核' },
              { value: 'processing', label: '链上处理中' },
              { value: 'approved', label: '已通过' },
              { value: 'rejected', label: '已拒绝' },
              { value: 'cancelled', label: '已取消' },
            ]}
          />
        </Space>
        <DataTable<RequestRow>
          queryKey={['admin', 'static-usdt-payout', 'requests', status]}
          endpoint="/api/admin/static-usdt-payout/requests"
          extraParams={status ? { status } : {}}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 70 },
            { title: '用户', dataIndex: 'username', width: 140 },
            { title: '金额$', dataIndex: 'amount_usd', width: 90 },
            { title: 'EP1', dataIndex: 'amount_ep1', width: 100 },
            {
              title: '钱包',
              dataIndex: 'wallet_address',
              ellipsis: true,
              render: (v: string) => v?.slice(0, 10) + '…' + v?.slice(-6),
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 90,
              render: (s: string) => {
                const t = STATUS_TAG[s] ?? { color: 'default', text: s };
                return <Tag color={t.color}>{t.text}</Tag>;
              },
            },
            { title: '批次', dataIndex: 'batch_code', width: 160 },
            {
              title: '操作',
              width: 160,
              render: (_: unknown, row: RequestRow) =>
                row.status === 'pending' ? (
                  <Space>
                    {canApprove && (
                      <Button size="small" type="primary" onClick={() => setApproveTarget(row)}>
                        通过
                      </Button>
                    )}
                    {canReject && (
                      <Button size="small" danger onClick={() => setRejectTarget(row)}>
                        拒绝
                      </Button>
                    )}
                  </Space>
                ) : (
                  row.payout_ref || row.review_note || '—'
                ),
            },
          ]}
        />
      </Card>

      <Modal
        title={`通过兑付 #${approveTarget?.id ?? ''}`}
        open={Boolean(approveTarget)}
        onOk={approve}
        confirmLoading={approving}
        onCancel={() => {
          setApproveTarget(null);
          setPayoutRef('');
        }}
        okText="确认通过"
      >
        {autoMockPayout ? (
          <Typography.Paragraph>
            确认后系统将自动 mint ${approveTarget?.amount_usd ?? '0'} MockUSDT 到用户绑定钱包
            <Typography.Text code>{approveTarget?.wallet_address}</Typography.Text>
            ，链上成功后自动填写交易哈希。
          </Typography.Paragraph>
        ) : (
          <>
            <Typography.Paragraph>
              请确认已从静态兑付地址打 USDT 到用户钱包，并填写链上交易哈希。
            </Typography.Paragraph>
            <Input
              placeholder="payout_ref / tx hash"
              value={payoutRef}
              onChange={(e) => setPayoutRef(e.target.value)}
            />
          </>
        )}
      </Modal>

      <Modal
        title={`拒绝兑付 #${rejectTarget?.id ?? ''}`}
        open={Boolean(rejectTarget)}
        onOk={reject}
        onCancel={() => {
          setRejectTarget(null);
          setRejectReason('');
        }}
        okText="确认拒绝"
        okButtonProps={{ danger: true }}
      >
        <Input.TextArea
          rows={3}
          placeholder="拒绝原因（将退回 EP1）"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}
